import { useId, useState } from 'react';
import type { ChangeEvent } from 'react';

import { getInputControlState, InputControl } from 'ui/forms/input-control';
import {
  INPUT_DEFAULT_PLACEHOLDER,
  INPUT_DEFAULT_TEST_ID,
} from 'ui/input/input.constants';
import {
  InputAdornmentSlot,
  InputDirtyDot,
  InputField,
} from 'ui/input/input.styles';
import type { InputProps } from 'ui/input/input.types';

const Input = ({
  label,
  'aria-label': ariaLabel,
  value,
  defaultValue,
  onChange,
  onBlur,
  onFocus,
  onKeyDown,
  id,
  name,
  type = 'text',
  placeholder = INPUT_DEFAULT_PLACEHOLDER,
  autoComplete,
  inputMode,
  maxLength,
  showCharCount = true,
  size = 'large',
  textAlign = 'left',
  disabled,
  required,
  error,
  errorMessage,
  supportiveText,
  helperText,
  startAdornment,
  endAdornment,
  dirty,
  ref,
  sx,
  controlSx,
  'data-testid': dataTestId = INPUT_DEFAULT_TEST_ID,
}: InputProps) => {
  const generatedId = useId();
  const fieldId = id || generatedId;
  const [uncontrolledLength, setUncontrolledLength] = useState(
    defaultValue?.length ?? 0
  );
  const isRequired = !!required && !disabled;
  const visibleHelperText = disabled ? undefined : helperText;
  const { isError, describedBy } = getInputControlState({
    id: fieldId,
    disabled,
    error,
    errorMessage,
    supportiveText,
    helperText: visibleHelperText,
  });
  const valueLength =
    value === undefined ? uncontrolledLength : String(value ?? '').length;
  const counterText =
    showCharCount && maxLength && !disabled
      ? `${valueLength} of ${maxLength} characters used`
      : undefined;
  const hasEndAdornment = !!endAdornment || !!dirty;
  const inputProps = {
    maxLength,
    inputMode,
    'aria-label': ariaLabel,
    'aria-required': isRequired || undefined,
    'aria-invalid': isError || undefined,
    'data-testid': dataTestId,
  };

  const handleChange = (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    if (value === undefined) setUncontrolledLength(event.target.value.length);

    onChange?.(event as ChangeEvent<HTMLInputElement>);
  };

  return (
    <InputControl
      id={fieldId}
      size={size}
      label={label}
      required={isRequired}
      disabled={disabled}
      error={error}
      errorMessage={errorMessage}
      supportiveText={supportiveText}
      helperText={visibleHelperText}
      counterText={counterText}
      sx={sx}
      data-testid={dataTestId}
    >
      <InputField
        fullWidth
        fieldSize={size}
        size={size}
        textAlign={textAlign}
        id={fieldId}
        name={name}
        type={type}
        value={value}
        defaultValue={defaultValue}
        placeholder={placeholder}
        autoComplete={autoComplete}
        disabled={disabled}
        inputRef={ref}
        onChange={handleChange}
        onBlur={onBlur}
        onFocus={onFocus}
        onKeyDown={onKeyDown}
        aria-describedby={describedBy}
        sx={controlSx}
        startAdornment={
          !!startAdornment && (
            <InputAdornmentSlot
              position="start"
              fieldSize={size}
              data-testid={`${dataTestId}-start-adornment`}
            >
              {startAdornment}
            </InputAdornmentSlot>
          )
        }
        endAdornment={
          hasEndAdornment && (
            <InputAdornmentSlot
              position="end"
              fieldSize={size}
              data-testid={`${dataTestId}-end-adornment`}
            >
              {endAdornment}
              {dirty && (
                <InputDirtyDot
                  role="img"
                  aria-label="Unsaved change"
                  data-testid={`${dataTestId}-dirty-indicator`}
                />
              )}
            </InputAdornmentSlot>
          )
        }
        slotProps={{ input: inputProps }}
      />
    </InputControl>
  );
};

export default Input;
